import React from 'react';
import { API_URL } from '../config';
import { ApiError, toApiError } from '../lib/api-error';
import type { WorkflowAutomationStrategy } from '../types/workflows';
import type { BrowserEngine, BrowserMode, BrowserViewportPreset } from '@chatsdet/types';
import type { ExecutionEvent } from '../ui/components/ExecutionMonitor';
import type { ScreenshotItem } from '@chatsdet/ui';

/**
 * Single shape returned by the server's deterministic `WorkflowValidationService`.
 * The FE renders these issues in the same banner/dialog the legacy client
 * validator used to populate.
 */
export type WorkflowValidationServerIssue = {
  nodeId: string | null;
  nodeType: string | null;
  field: string;
  message: string;
  code?: string;
  severity?: 'error' | 'warning';
};

export class WorkflowValidationError extends Error {
  readonly issues: WorkflowValidationServerIssue[];

  constructor(opts: { message: string; issues: WorkflowValidationServerIssue[] }) {
    super(opts.message);
    this.name = 'WorkflowValidationError';
    this.issues = opts.issues;
  }
}

function isWorkflowValidationIssue(value: unknown): value is WorkflowValidationServerIssue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const sev = record.severity;
  return (
    (record.nodeId === null || typeof record.nodeId === 'string') &&
    (record.nodeType === null || typeof record.nodeType === 'string') &&
    typeof record.field === 'string' &&
    typeof record.message === 'string' &&
    (record.code === undefined || typeof record.code === 'string') &&
    (sev === undefined || sev === 'error' || sev === 'warning')
  );
}

export function toWorkflowValidationError(error: ApiError): WorkflowValidationError | null {
  if (error.code !== 'WORKFLOW_VALIDATION_FAILED') return null;
  const details = error.details;
  const rawIssues = Array.isArray(details?.issues) ? details.issues : [];
  const issues = rawIssues.filter(isWorkflowValidationIssue);
  if (issues.length === 0) return null;
  return new WorkflowValidationError({ message: error.message, issues });
}
/**
 * Fetch run detail from API (uses existing endpoint).
 * Returns null if fetch fails or run not found.
 */
/** Metadata from step that we need for hydration */
type StepMetadataForHydration = {
  resumeAt?: string;
  delaySeconds?: number;
  [key: string]: unknown;
};

async function fetchRunDetail(
  workflowId: string,
  runId: string
): Promise<{
  status: string;
  recoveryPending?: boolean;
  steps: Array<{
    nodeId: string;
    status: string;
    metadataJson?: StepMetadataForHydration | null;
  }>;
} | null> {
  try {
    const response = await fetch(`${API_URL}/workflows/${workflowId}/runs/${runId}`, {
      credentials: 'include',
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.ok ? data.data.run : null;
  } catch {
    return null;
  }
}

type NodeStatus = {
  state: 'idle' | 'running' | 'waiting' | 'success' | 'failed' | 'warning' | 'skipped';
  message?: string;
  nodeName?: string;
  updatedAt: number;
  conditionResult?: 'true' | 'false';
  /** ISO timestamp when wait period ends (for wait nodes) */
  resumeAt?: string;
  /** Total delay in seconds (for wait nodes) */
  delaySeconds?: number;
};

function nodeStatusSignature(statuses: Map<string, NodeStatus>): string {
  return JSON.stringify(
    [...statuses.entries()]
      .map(([nodeId, status]) => [nodeId, status.state] as const)
      .sort(([a], [b]) => a.localeCompare(b))
  );
}

function stepStatusSignature(steps: Array<{ nodeId: string; status: string }>): string {
  return JSON.stringify(
    steps
      .map(({ nodeId, status }) => [nodeId, status === 'pending' ? 'idle' : status] as const)
      .sort(([a], [b]) => a.localeCompare(b))
  );
}

export function isFlowDesignerRunTerminal(run: {
  status: string;
  recoveryPending?: boolean;
}): boolean {
  return (
    ['success', 'failed', 'canceled'].includes(run.status) ||
    (run.status === 'inconclusive' && run.recoveryPending !== true)
  );
}

type SSEStateSetters = {
  setEvents: React.Dispatch<React.SetStateAction<ExecutionEvent[]>>;
  setCurrentStepId: React.Dispatch<React.SetStateAction<string | null>>;
  setCompletedNodeIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setFailedNodeIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setNodeStatus: (nodeId: string, next: NodeStatus | null) => void;
  setNodeStatuses: React.Dispatch<React.SetStateAction<Map<string, NodeStatus>>>;
  setScreenshotItems: React.Dispatch<React.SetStateAction<ScreenshotItem[]>>;
  setIsRunning: React.Dispatch<React.SetStateAction<boolean>>;
  eventSourceRef: React.MutableRefObject<EventSource | null>;
  /** The run this stream belongs to — terminal events for any other run are ignored. */
  connectedRunIdRef: React.MutableRefObject<string | null>;
  sinceCursorRef: React.MutableRefObject<string | null>;
  retryCountRef: React.MutableRefObject<number>;
  /** Epoch ms of the last received SSE event — the stale-stream watchdog reads this. */
  lastEventAtRef: React.MutableRefObject<number>;
  /** Clears a delayed reconnect hint when the stream delivers any event. */
  clearReconnectHint: () => void;
};

function setupSSEListeners(eventSource: EventSource, setters: SSEStateSetters) {
  const {
    setEvents,
    setCurrentStepId,
    setCompletedNodeIds,
    setFailedNodeIds,
    setNodeStatus,
    setNodeStatuses,
    setScreenshotItems,
    setIsRunning,
    eventSourceRef,
    connectedRunIdRef,
    sinceCursorRef,
    retryCountRef,
    lastEventAtRef,
    clearReconnectHint,
  } = setters;

  /**
   * A run driven from inside another run's job — an inline seed — shares the
   * consuming run's broker stream. Its terminal event must not be read as
   * "our run finished", or we tear the stream down while the run we're watching
   * is still going. Events with no `runId` predate the attribution and are
   * treated as ours.
   */
  const isForeignRunTerminal = (data: { runId?: unknown }): boolean =>
    typeof data.runId === 'string' &&
    connectedRunIdRef.current !== null &&
    data.runId !== connectedRunIdRef.current;

  // EventSource only delivers events whose names have a registered listener, so
  // anything missing from this list is emitted server-side, recorded on the run,
  // and silently never seen by the client — no error, no gap in the UI, just
  // absent data. `assertAllEventTypesSubscribed` below makes that a compile
  // error instead of a debugging session.
  const eventTypes = [
    'status',
    'attempt-reset',
    'log',
    'step-started',
    'step-progress',
    'step-completed',
    'step-failed',
    'step-skipped',
    'screenshot',
    'complete',
    'error',
    'canceled',
  ] as const;

  /**
   * Compile-time exhaustiveness: every `ExecutionEvent` variant must appear in
   * `eventTypes`. Adding a variant without subscribing to it now fails
   * typecheck with the missing name in the error.
   */
  type UnsubscribedEventType = Exclude<ExecutionEvent['type'], (typeof eventTypes)[number]>;
  // `[T] extends [never]` — the tuple wrapper is load-bearing. A naked
  // `T extends never` distributes over the union and collapses to `never`,
  // and typing the assertion as an ARRAY of the leftovers doesn't work either
  // (`[]` is assignable to every array type, so it never errors).
  const assertAllEventTypesSubscribed: [UnsubscribedEventType] extends [never] ? true : false =
    true;
  void assertAllEventTypesSubscribed;

  eventTypes.forEach((eventType) => {
    eventSource.addEventListener(eventType, (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data);

        if ((eventType === 'complete' || eventType === 'canceled') && isForeignRunTerminal(data)) {
          return;
        }

        const event = {
          type: eventType,
          data,
        } as ExecutionEvent;

        setEvents((prev) => (eventType === 'attempt-reset' ? [event] : [...prev, event]));

        // Update since cursor and reset retry backoff on every received event
        sinceCursorRef.current = new Date().toISOString();
        retryCountRef.current = 0;
        // The stream is alive: stamp liveness for the stale-stream watchdog and
        // clear any delayed reconnect hint it raised.
        lastEventAtRef.current = Date.now();
        clearReconnectHint();

        // A fresh full-run attempt has no relationship to first-attempt UI
        // evidence. Clear it atomically when the server resets DB artifacts.
        if (eventType === 'attempt-reset') {
          setCurrentStepId(null);
          setCompletedNodeIds(new Set());
          setFailedNodeIds(new Set());
          setNodeStatuses(new Map());
          setScreenshotItems([]);
          setIsRunning(true);
        } else if (eventType === 'step-started') {
          setCurrentStepId(data.nodeId);
          // Clear any previously-running node so only one node shows "Running"
          setNodeStatuses((prev) => {
            const next = new Map(prev);
            for (const [id, status] of next) {
              if (id !== data.nodeId && status.state === 'running') {
                next.delete(id);
              }
            }
            return next;
          });
          setNodeStatus(data.nodeId, {
            state: 'running',
            message: data.message,
            nodeName: data.nodeName,
            updatedAt: Date.now(),
          });
          setCompletedNodeIds((prev) => {
            const next = new Set(prev);
            next.delete(data.nodeId);
            return next;
          });
          setFailedNodeIds((prev) => {
            const next = new Set(prev);
            next.delete(data.nodeId);
            return next;
          });
        } else if (eventType === 'step-completed') {
          setCompletedNodeIds((prev) => new Set([...prev, data.nodeId]));
          setNodeStatus(data.nodeId, {
            state: 'success',
            message: data.message,
            nodeName: data.nodeName,
            updatedAt: Date.now(),
            conditionResult: data.conditionResult,
          });
          setCurrentStepId((prev) => (prev === data.nodeId ? null : prev));
        } else if (eventType === 'step-skipped') {
          setNodeStatus(data.nodeId, {
            state: 'skipped',
            message: data.message,
            nodeName: data.nodeName,
            updatedAt: Date.now(),
          });
          setCurrentStepId((prev) => (prev === data.nodeId ? null : prev));
        } else if (eventType === 'step-failed') {
          // Only warn-severity assertion failures render as a warning (run still
          // passes). fail-severity is also non-blocking (execution continues) but
          // the step IS failed and the run finishes marked failed — mirror the
          // persisted-metadata reconstruction below, which gates warning state on
          // assertionSeverity === 'warn'.
          const isWarnAssertion = !!data.nonBlocking && data.assertionSeverity === 'warn';
          if (!isWarnAssertion) {
            setFailedNodeIds((prev) => new Set([...prev, data.nodeId]));
          }
          setNodeStatus(data.nodeId, {
            state: isWarnAssertion ? 'warning' : 'failed',
            message: data.message || data.error,
            nodeName: data.nodeName,
            updatedAt: Date.now(),
          });
          setCurrentStepId((prev) => (prev === data.nodeId ? null : prev));
          if (!data.nonBlocking) {
            setIsRunning(false);
            eventSource.close();
            eventSourceRef.current = null;
          }
        } else if (eventType === 'step-progress') {
          const nextState = data.status === 'waiting' ? 'waiting' : 'running';

          // Parse resumeAt from message for wait nodes (format: "Waiting until <ISO timestamp>")
          let resumeAt: string | undefined;
          if (data.status === 'waiting' && data.message) {
            const match = data.message.match(/Waiting until (.+)/);
            if (match?.[1]) {
              resumeAt = match[1];
            }
          }

          setNodeStatus(data.nodeId, {
            state: nextState,
            message: data.message || data.status,
            nodeName: data.nodeName,
            updatedAt: Date.now(),
            resumeAt,
          });
        } else if (eventType === 'screenshot') {
          const s3Key: string | undefined = data.s3Key;
          let src: string | null = null;

          if (s3Key) {
            const encodedKey = encodeURIComponent(s3Key);
            // Rendered into `<img src>` — carry the org as a query param.
            src = `${API_URL}/screenshots/${encodedKey}`;
          } else if (typeof data.image === 'string') {
            src = data.image;
          }

          if (src) {
            setScreenshotItems((prev) => [
              ...prev,
              {
                id: `screenshot-${Date.now()}-${data.nodeId}`,
                src,
                caption: data.nodeName || null,
              },
            ]);
          }
        } else if (eventType === 'complete' || eventType === 'error' || eventType === 'canceled') {
          if (eventType === 'canceled') {
            // Transition any in-progress nodes to 'skipped'
            setNodeStatuses((prev) => {
              const updated = new Map(prev);
              for (const [nodeId, status] of updated) {
                if (status.state === 'running' || status.state === 'waiting') {
                  updated.set(nodeId, { ...status, state: 'skipped', updatedAt: Date.now() });
                }
              }
              return updated;
            });
          }
          setIsRunning(false);
          setCurrentStepId(null);
          eventSource.close();
        }
      } catch {
        // Ignore malformed events; SSE is best-effort.
      }
    });
  });

  eventSource.onerror = () => {
    setIsRunning(false);
    setCurrentStepId(null);
    eventSource.close();

    setEvents((prev) => [
      ...prev,
      {
        type: 'error',
        data: { message: 'Connection error. Please try again.' },
      },
    ]);
  };
}

export function useWorkflowExecution(workflowId: string | null) {
  const [isRunning, setIsRunning] = React.useState(false);
  const [runId, setRunId] = React.useState<string | null>(null);
  const [events, setEvents] = React.useState<ExecutionEvent[]>([]);
  const [currentStepId, setCurrentStepId] = React.useState<string | null>(null);
  const [completedNodeIds, setCompletedNodeIds] = React.useState<Set<string>>(new Set());
  const [failedNodeIds, setFailedNodeIds] = React.useState<Set<string>>(new Set());
  const [nodeStatuses, setNodeStatuses] = React.useState<Map<string, NodeStatus>>(new Map());
  const [screenshotItems, setScreenshotItems] = React.useState<ScreenshotItem[]>([]);
  // True after the stale-stream watchdog has been resyncing + reopening the SSE
  // for long enough to warrant a subtle hint, never an error.
  const [isReconnecting, setIsReconnecting] = React.useState(false);
  const eventSourceRef = React.useRef<EventSource | null>(null);
  // Guard to prevent concurrent execution calls (synchronous, unlike isRunning state)
  const isStartingRef = React.useRef(false);
  // Track the run ID we're currently connected to (to prevent duplicate reconnections)
  const connectedRunIdRef = React.useRef<string | null>(null);
  // ISO timestamp of last received SSE event — used as `since` cursor on reconnect
  const sinceCursorRef = React.useRef<string | null>(null);
  // Exponential backoff retry count for SSE reconnection
  const retryCountRef = React.useRef(0);
  // Epoch ms of the last received SSE event. The watchdog treats a long gap here
  // (with the run still in-flight per the DB) as a silent stall and reconnects.
  const lastEventAtRef = React.useRef<number>(0);
  // Last durable run status observed by the watchdog. A status transition is
  // meaningful progress even when no step row has changed yet.
  const lastPolledRunStatusRef = React.useRef<string | null>(null);
  const lastPolledStepSignatureRef = React.useRef<string | null>(null);
  // A first poll establishes the durable baseline. This prevents pre-created
  // pending rows from looking like unseen progress when the stream is still in
  // its preparation phase. Later changes remain pending until the client has
  // observed the matching step state.
  const durableStepDivergenceRef = React.useRef(false);
  // Synchronous mirror of `isReconnecting` for use inside SSE listeners.
  const isReconnectingRef = React.useRef(false);
  // The watchdog reconnects immediately, but avoid flashing a reconnect hint
  // for ordinary resyncs that recover within a few seconds.
  const reconnectHintTimerRef = React.useRef<number | null>(null);

  const clearReconnectHint = React.useCallback(() => {
    if (reconnectHintTimerRef.current !== null) {
      window.clearTimeout(reconnectHintTimerRef.current);
      reconnectHintTimerRef.current = null;
    }
    isReconnectingRef.current = false;
    setIsReconnecting(false);
  }, []);

  const setNodeStatus = React.useCallback((nodeId: string, next: NodeStatus | null) => {
    setNodeStatuses((prev) => {
      const updated = new Map(prev);
      if (next) {
        updated.set(nodeId, next);
      } else {
        updated.delete(nodeId);
      }
      return updated;
    });
  }, []);

  const sseSetters: SSEStateSetters = React.useMemo(
    () => ({
      setEvents,
      setCurrentStepId,
      setCompletedNodeIds,
      setFailedNodeIds,
      setNodeStatus,
      setNodeStatuses,
      setScreenshotItems,
      setIsRunning,
      eventSourceRef,
      connectedRunIdRef,
      sinceCursorRef,
      retryCountRef,
      lastEventAtRef,
      clearReconnectHint,
    }),
    [clearReconnectHint, setNodeStatus]
  );

  /**
   * Hydrate node statuses from step data (e.g., from run detail API).
   * This populates completedNodeIds, failedNodeIds, nodeStatuses, and currentStepId
   * based on existing step progress.
   */
  const hydrateFromSteps = React.useCallback(
    (
      steps: Array<{
        nodeId: string;
        status: string;
        metadataJson?: StepMetadataForHydration | null;
      }>
    ) => {
      const completed = new Set<string>();
      const failed = new Set<string>();
      const statuses = new Map<string, NodeStatus>();
      let currentId: string | null = null;

      for (const step of steps) {
        const isWarnAssertion =
          step.status === 'failed' &&
          step.metadataJson?.nonBlocking === true &&
          step.metadataJson?.assertionSeverity === 'warn';

        const state: NodeStatus['state'] =
          step.status === 'success'
            ? 'success'
            : step.status === 'failed'
              ? isWarnAssertion
                ? 'warning'
                : 'failed'
              : step.status === 'running'
                ? 'running'
                : step.status === 'waiting'
                  ? 'waiting'
                  : step.status === 'skipped'
                    ? 'skipped'
                    : 'idle';

        if (state === 'success') completed.add(step.nodeId);
        if (state === 'failed') failed.add(step.nodeId);
        if (state === 'running') currentId = step.nodeId;

        // Extract resumeAt and delaySeconds from metadata for waiting steps
        const nodeStatus: NodeStatus = { state, updatedAt: Date.now() };
        if (state === 'waiting' && step.metadataJson) {
          const metadata = step.metadataJson;
          if (typeof metadata.resumeAt === 'string') {
            nodeStatus.resumeAt = metadata.resumeAt;
          }
          if (typeof metadata.delaySeconds === 'number') {
            nodeStatus.delaySeconds = metadata.delaySeconds;
          }
        }

        statuses.set(step.nodeId, nodeStatus);
      }

      setCompletedNodeIds(completed);
      setFailedNodeIds(failed);
      setNodeStatuses(statuses);
      setCurrentStepId(currentId);
    },
    []
  );

  // Reconnect to an existing running workflow run (e.g., on page reload).
  // `force` bypasses the "already connected / already running" guards so the
  // stale-stream watchdog can tear down a silently-stalled stream and reopen it
  // while the UI still believes the run is live.
  const reconnectToRun = React.useCallback(
    async (existingRunId: string, opts?: { force?: boolean }) => {
      const force = opts?.force ?? false;
      // Guard: already connected to this run
      if (!force && connectedRunIdRef.current === existingRunId) {
        return;
      }
      // Guard: already running (started via startExecution)
      if (!force && isRunning && runId) {
        return;
      }

      console.log('[useWorkflowExecution] reconnectToRun called', { existingRunId });

      // Close any existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      // 1. Fetch current state from database FIRST (using existing endpoint)
      const state = workflowId ? await fetchRunDetail(workflowId, existingRunId) : null;

      // 2. If run is already complete, just hydrate and exit
      if (state && isFlowDesignerRunTerminal(state)) {
        console.log('[useWorkflowExecution] run already complete, hydrating state', {
          status: state.status,
        });
        hydrateFromSteps(state.steps);
        setRunId(existingRunId);
        setIsRunning(false);
        clearReconnectHint();
        connectedRunIdRef.current = null;
        return;
      }

      // 3. Hydrate existing progress before connecting to SSE
      if (state?.steps) {
        console.log('[useWorkflowExecution] hydrating existing progress', {
          stepCount: state.steps.length,
        });
        hydrateFromSteps(state.steps);
      }

      // 4. Set state and connect to SSE
      connectedRunIdRef.current = existingRunId;
      setRunId(existingRunId);
      setIsRunning(true);
      // Clear events for clean slate (but don't clear node statuses - we just hydrated them!)
      setEvents([]);
      setScreenshotItems([]);

      // Connect to SSE stream (streamId === runId), with `since` cursor for gap replay
      const streamUrl = sinceCursorRef.current
        ? `${API_URL}/jobs/${existingRunId}/stream?since=${encodeURIComponent(sinceCursorRef.current)}`
        : `${API_URL}/jobs/${existingRunId}/stream`;
      // Stamp liveness up front so the watchdog gives the fresh stream a full
      // staleness window before judging it stalled again.
      lastEventAtRef.current = Date.now();
      const eventSource = new EventSource(streamUrl, { withCredentials: true });
      eventSourceRef.current = eventSource;

      // Timeout: if no events received within 5 seconds, check DB for completion
      let receivedEvent = false;
      const timeoutId = window.setTimeout(async () => {
        if (!receivedEvent && eventSourceRef.current === eventSource) {
          console.log('[useWorkflowExecution] reconnect timeout - checking DB for completion');
          // Check if run completed during the wait
          const freshState = workflowId ? await fetchRunDetail(workflowId, existingRunId) : null;
          if (freshState && isFlowDesignerRunTerminal(freshState)) {
            console.log(
              '[useWorkflowExecution] run completed during timeout, hydrating final state'
            );
            eventSource.close();
            eventSourceRef.current = null;
            connectedRunIdRef.current = null;
            hydrateFromSteps(freshState.steps);
            setIsRunning(false);
          } else if (!freshState) {
            // Can't reach server, reset state
            console.log('[useWorkflowExecution] reconnect timeout - no state available, resetting');
            eventSource.close();
            eventSourceRef.current = null;
            connectedRunIdRef.current = null;
            setIsRunning(false);
            setRunId(null);
          }
          // If freshState shows still running, keep connection open - SSE might still work
        }
      }, 5000);

      // Track event receipt to clear timeout
      eventSource.addEventListener('message', () => {
        receivedEvent = true;
        window.clearTimeout(timeoutId);
      });
      // Also clear timeout on any named event
      const clearTimeoutOnEvent = () => {
        receivedEvent = true;
        window.clearTimeout(timeoutId);
      };
      [
        'status',
        'attempt-reset',
        'step-started',
        'step-progress',
        'step-completed',
        'step-failed',
        'step-skipped',
        'complete',
        'error',
        'canceled',
      ].forEach((type) => eventSource.addEventListener(type, clearTimeoutOnEvent, { once: true }));

      setupSSEListeners(eventSource, sseSetters);

      // 5. Override onerror with exponential backoff reconnection
      eventSource.onerror = () => {
        eventSource.close();
        eventSourceRef.current = null;

        const currentRunId = connectedRunIdRef.current;
        const currentWorkflowId = workflowId;

        if (!currentRunId || !currentWorkflowId) {
          setIsRunning(false);
          return;
        }

        const retryCount = retryCountRef.current;
        const maxRetries = 5;

        // Check database to see if run actually finished
        fetchRunDetail(currentWorkflowId, currentRunId)
          .then((freshState) => {
            if (!freshState) {
              // Can't reach server — exhaust retries before showing error
              if (retryCount < maxRetries) {
                const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
                retryCountRef.current = retryCount + 1;
                console.log('[useWorkflowExecution] Server unreachable, retrying', {
                  retryCount,
                  delay,
                });
                connectedRunIdRef.current = null;
                setTimeout(() => {
                  reconnectToRun(currentRunId);
                }, delay);
              } else {
                setIsRunning(false);
                connectedRunIdRef.current = null;
                setEvents((prev) => [
                  ...prev,
                  { type: 'error', data: { message: 'Connection lost. Please refresh.' } },
                ]);
              }
              return;
            }

            if (isFlowDesignerRunTerminal(freshState)) {
              // Run finished while disconnected - hydrate final state
              console.log('[useWorkflowExecution] Run completed during disconnect, hydrating');
              hydrateFromSteps(freshState.steps);
              setIsRunning(false);
              connectedRunIdRef.current = null;
            } else {
              // Still running - reconnect with exponential backoff
              if (retryCount < maxRetries) {
                const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
                retryCountRef.current = retryCount + 1;
                console.log('[useWorkflowExecution] Run still active, reconnecting', {
                  retryCount,
                  delay,
                });
                connectedRunIdRef.current = null;
                setTimeout(() => {
                  reconnectToRun(currentRunId);
                }, delay);
              } else {
                setIsRunning(false);
                connectedRunIdRef.current = null;
                setEvents((prev) => [
                  ...prev,
                  {
                    type: 'error',
                    data: { message: 'Connection lost after multiple retries. Please refresh.' },
                  },
                ]);
              }
            }
          })
          .catch(() => {
            if (retryCount < maxRetries) {
              const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
              retryCountRef.current = retryCount + 1;
              connectedRunIdRef.current = null;
              setTimeout(() => {
                reconnectToRun(currentRunId);
              }, delay);
            } else {
              setIsRunning(false);
              connectedRunIdRef.current = null;
              setEvents((prev) => [
                ...prev,
                { type: 'error', data: { message: 'Connection lost. Please refresh.' } },
              ]);
            }
          });
      };
    },
    [workflowId, isRunning, runId, sseSetters, hydrateFromSteps, clearReconnectHint]
  );

  const startExecution = React.useCallback(
    async (options?: {
      automationStrategy?: WorkflowAutomationStrategy;
      browserMode?: BrowserMode;
      browserEngine?: BrowserEngine;
      viewportSize?: BrowserViewportPreset;
      modelId?: string;
      inputValues?: Record<string, string>;
      scenarioId?: string;
      environmentId?: string;
      environmentStageId?: string;
      /** Superadmin-only: force the single-agent segment path (server gates on superadmin). */
      forceSingleAgentSegment?: boolean;
    }) => {
      console.log('[useWorkflowExecution] startExecution entry', {
        workflowId,
        isRunning,
        isStarting: isStartingRef.current,
        connectedRunId: connectedRunIdRef.current,
      });

      if (!workflowId) return null;

      // Guard against concurrent execution calls
      if (isStartingRef.current) {
        console.warn('[useWorkflowExecution] startExecution blocked - already starting');
        return null;
      }
      if (isRunning) {
        console.warn('[useWorkflowExecution] startExecution blocked - already running');
        return null;
      }

      // Set synchronous guard immediately
      isStartingRef.current = true;
      console.log('[useWorkflowExecution] startExecution called', { workflowId });

      setIsRunning(true);
      setRunId(null);
      setEvents([]);
      setCurrentStepId(null);
      setCompletedNodeIds(new Set());
      setFailedNodeIds(new Set());
      setNodeStatuses(new Map());
      setScreenshotItems([]);
      connectedRunIdRef.current = null;
      sinceCursorRef.current = null;
      retryCountRef.current = 0;
      lastEventAtRef.current = Date.now();
      lastPolledRunStatusRef.current = null;
      lastPolledStepSignatureRef.current = null;
      durableStepDivergenceRef.current = false;
      clearReconnectHint();

      try {
        // Step 1: Kickoff execution. Tag the direct surface for run-history
        // filtering; the server treats absent/unknown triggerSource as `api`.
        const requestInit: RequestInit = {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            triggerSource: 'flow_designer',
            ...(options?.automationStrategy
              ? { automationStrategy: options.automationStrategy }
              : {}),
            ...(options?.browserMode ? { browserMode: options.browserMode } : {}),
            ...(options?.browserEngine ? { browserEngine: options.browserEngine } : {}),
            ...(options?.viewportSize ? { viewportSize: options.viewportSize } : {}),
            ...(options?.modelId ? { modelId: options.modelId } : {}),
            ...(options?.inputValues ? { inputValues: options.inputValues } : {}),
            ...(options?.scenarioId ? { scenarioId: options.scenarioId } : {}),
            ...(options?.environmentId ? { environmentId: options.environmentId } : {}),
            ...(options?.environmentStageId
              ? { environmentStageId: options.environmentStageId }
              : {}),
            ...(options?.forceSingleAgentSegment ? { forceSingleAgentSegment: true } : {}),
          }),
        };

        const response = await fetch(`${API_URL}/workflows/${workflowId}/execute`, requestInit);

        if (!response.ok) {
          const apiError = await toApiError(response, 'Failed to start execution');
          if (apiError instanceof ApiError) {
            const detError = toWorkflowValidationError(apiError);
            if (detError) throw detError;
          }
          throw apiError;
        }

        const body = (await response.json()) as { runId?: unknown; streamId?: unknown };
        const nextRunId = typeof body.runId === 'string' ? body.runId : null;
        if (!nextRunId) {
          throw new Error('Missing runId from execution kickoff');
        }
        setRunId(nextRunId);
        connectedRunIdRef.current = nextRunId;

        // Clear the starting guard - execution has successfully started
        isStartingRef.current = false;

        // Step 2: Connect to event stream using streamId (falls back to runId)
        const streamId = typeof body.streamId === 'string' ? body.streamId : nextRunId;
        const streamUrl = `${API_URL}/jobs/${streamId}/stream`;
        lastEventAtRef.current = Date.now();
        const eventSource = new EventSource(streamUrl, { withCredentials: true });

        eventSourceRef.current = eventSource;

        setupSSEListeners(eventSource, sseSetters);

        // Override onerror with exponential backoff reconnection
        eventSource.onerror = () => {
          eventSource.close();
          eventSourceRef.current = null;

          const currentRunId = connectedRunIdRef.current;
          const currentWorkflowId = workflowId;

          if (!currentRunId || !currentWorkflowId) {
            setIsRunning(false);
            return;
          }

          const retryCount = retryCountRef.current;
          const maxRetries = 5;

          fetchRunDetail(currentWorkflowId, currentRunId)
            .then((freshState) => {
              if (!freshState) {
                if (retryCount < maxRetries) {
                  const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
                  retryCountRef.current = retryCount + 1;
                  console.log('[useWorkflowExecution] Server unreachable, retrying', {
                    retryCount,
                    delay,
                  });
                  connectedRunIdRef.current = null;
                  setTimeout(() => {
                    reconnectToRun(currentRunId);
                  }, delay);
                } else {
                  setIsRunning(false);
                  connectedRunIdRef.current = null;
                  setEvents((prev) => [
                    ...prev,
                    { type: 'error', data: { message: 'Connection lost. Please refresh.' } },
                  ]);
                }
                return;
              }

              if (isFlowDesignerRunTerminal(freshState)) {
                console.log('[useWorkflowExecution] Run completed during disconnect, hydrating');
                hydrateFromSteps(freshState.steps);
                setIsRunning(false);
                connectedRunIdRef.current = null;
              } else {
                if (retryCount < maxRetries) {
                  const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
                  retryCountRef.current = retryCount + 1;
                  console.log('[useWorkflowExecution] Run still active, reconnecting', {
                    retryCount,
                    delay,
                  });
                  connectedRunIdRef.current = null;
                  setTimeout(() => {
                    reconnectToRun(currentRunId);
                  }, delay);
                } else {
                  setIsRunning(false);
                  connectedRunIdRef.current = null;
                  setEvents((prev) => [
                    ...prev,
                    {
                      type: 'error',
                      data: { message: 'Connection lost after multiple retries. Please refresh.' },
                    },
                  ]);
                }
              }
            })
            .catch(() => {
              if (retryCount < maxRetries) {
                const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
                retryCountRef.current = retryCount + 1;
                connectedRunIdRef.current = null;
                setTimeout(() => {
                  reconnectToRun(currentRunId);
                }, delay);
              } else {
                setIsRunning(false);
                connectedRunIdRef.current = null;
                setEvents((prev) => [
                  ...prev,
                  { type: 'error', data: { message: 'Connection lost. Please refresh.' } },
                ]);
              }
            });
        };

        return nextRunId;
      } catch (err: unknown) {
        isStartingRef.current = false;
        connectedRunIdRef.current = null;
        setIsRunning(false);
        setRunId(null);
        if (err instanceof WorkflowValidationError) {
          setEvents([]);
          throw err;
        }
        setEvents([
          {
            type: 'error',
            data: { message: err instanceof Error ? err.message : 'Failed to start execution' },
          },
        ]);
        return null;
      }
    },
    [workflowId, isRunning, sseSetters, hydrateFromSteps, reconnectToRun, clearReconnectHint]
  );

  const stopExecution = React.useCallback(async () => {
    // 1. Call cancel endpoint if we have an active run
    if (workflowId && runId) {
      try {
        await fetch(`${API_URL}/workflows/${workflowId}/runs/${runId}/cancel`, {
          method: 'POST',
          credentials: 'include',
        });
      } catch (err) {
        console.error('Failed to cancel workflow run:', err);
      }
    }

    // 2. Close the EventSource
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // 3. Update local state — transition any in-progress nodes to 'skipped'
    setNodeStatuses((prev) => {
      const updated = new Map(prev);
      for (const [nodeId, status] of updated) {
        if (status.state === 'running' || status.state === 'waiting') {
          updated.set(nodeId, { ...status, state: 'skipped', updatedAt: Date.now() });
        }
      }
      return updated;
    });
    setIsRunning(false);
    setCurrentStepId(null);
    connectedRunIdRef.current = null;
    clearReconnectHint();
  }, [workflowId, runId, clearReconnectHint]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      clearReconnectHint();
    };
  }, [clearReconnectHint]);

  // Reset state when workflow changes
  React.useEffect(() => {
    setEvents([]);
    setIsRunning(false);
    setRunId(null);
    setCurrentStepId(null);
    setCompletedNodeIds(new Set());
    setFailedNodeIds(new Set());
    setNodeStatuses(new Map());
    setScreenshotItems([]);
    isStartingRef.current = false;
    connectedRunIdRef.current = null;
    sinceCursorRef.current = null;
    retryCountRef.current = 0;
    lastEventAtRef.current = 0;
    lastPolledRunStatusRef.current = null;
    lastPolledStepSignatureRef.current = null;
    durableStepDivergenceRef.current = false;
    clearReconnectHint();
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, [workflowId, clearReconnectHint]);

  /**
   * Check for an active run on page load/mount and reconnect if found.
   * This detects running executions by fetching the runs list and looking
   * for runs with status 'running' or 'queued'.
   */
  const checkForActiveRun = React.useCallback(async () => {
    if (!workflowId) return;

    // Don't check if already running
    if (isRunning || isStartingRef.current) return;

    try {
      // Fetch first page of runs (most recent first) using existing endpoint
      const url = new URL(`${API_URL}/workflows/${workflowId}/runs`);
      url.searchParams.set('page', '1');
      url.searchParams.set('pageSize', '5'); // Only need to check recent runs

      const response = await fetch(url.toString(), { credentials: 'include' });
      if (!response.ok) return;

      const data = await response.json();
      if (!data.ok) return;

      // Only check the most recent run — old stuck runs shouldn't trigger "Running" UI
      const runs = data.data?.runs ?? [];
      const mostRecent = runs[0];
      const activeRun =
        mostRecent &&
        (mostRecent.status === 'running' ||
          mostRecent.status === 'queued' ||
          (mostRecent.status === 'inconclusive' && mostRecent.recoveryPending === true))
          ? mostRecent
          : undefined;

      if (activeRun) {
        console.log('[useWorkflowExecution] Found active run, reconnecting', {
          runId: activeRun.id,
        });
        await reconnectToRun(activeRun.id);
      }
    } catch (err) {
      console.error('[useWorkflowExecution] Failed to check for active run:', err);
    }
  }, [workflowId, isRunning, reconnectToRun]);

  // ── Stale-stream watchdog ────────────────────────────────────────────────
  // The SSE `onerror` recovery above only fires on a *hard* disconnect. A stream
  // can also silently stall — the connection stays open but stops delivering
  // events and never fires `onerror` (LB idle timeout, network blackhole, a
  // worker gone quiet). When that happens mid-run, `isRunning` would stay true
  // forever and pin the panel to "Running". While a run is live we run a
  // secondary poll against the DB (the source of truth) to:
  //   • finalize the UI + close the stream when the run has reached a terminal
  //     state, and
  //   • when the stream has gone silent but the run is still in-flight, resync
  //     the panel from the DB and force a reconnect only when durable progress
  //     diverges from what the client has observed.
  React.useEffect(() => {
    if (!isRunning || !runId || !workflowId) return;

    const VERIFY_INTERVAL_MS = 7000; // how often we re-verify against the DB
    const STALE_STREAM_MS = 20000; // no SSE event for this long ⇒ stream stalled

    const activeWorkflowId = workflowId;
    const activeRunId = runId;
    let disposed = false;

    const intervalId = window.setInterval(() => {
      void (async () => {
        const fresh = await fetchRunDetail(activeWorkflowId, activeRunId);
        // Null = server unreachable/transient. Leave it to the `onerror` backoff
        // path, which owns true connectivity loss — don't double-handle here.
        if (!fresh || disposed) return;

        // Terminal in the DB: end the UI and the stream, stop the poll.
        if (isFlowDesignerRunTerminal(fresh)) {
          hydrateFromSteps(fresh.steps);
          setIsRunning(false);
          setCurrentStepId(null);
          clearReconnectHint();
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          connectedRunIdRef.current = null;
          return;
        }

        const freshStepSignature = stepStatusSignature(fresh.steps);
        const previousStepSignature = lastPolledStepSignatureRef.current;
        lastPolledStepSignatureRef.current = freshStepSignature;
        if (previousStepSignature !== null && previousStepSignature !== freshStepSignature) {
          durableStepDivergenceRef.current = true;
        }
        if (freshStepSignature === nodeStatusSignature(nodeStatuses)) {
          durableStepDivergenceRef.current = false;
        }
        const durableStatusChanged =
          lastPolledRunStatusRef.current !== null &&
          lastPolledRunStatusRef.current !== fresh.status;
        lastPolledRunStatusRef.current = fresh.status;

        // Still running but the stream has gone quiet. Only resync when the
        // durable run has progressed beyond what the client observed; a quiet
        // preparation phase can legitimately last several minutes.
        const streamSilentFor = Date.now() - lastEventAtRef.current;
        if (
          streamSilentFor > STALE_STREAM_MS &&
          (durableStepDivergenceRef.current || durableStatusChanged) &&
          !isReconnectingRef.current
        ) {
          hydrateFromSteps(fresh.steps);
          isReconnectingRef.current = true;
          if (reconnectHintTimerRef.current !== null) {
            window.clearTimeout(reconnectHintTimerRef.current);
          }
          reconnectHintTimerRef.current = window.setTimeout(() => {
            reconnectHintTimerRef.current = null;
            if (isReconnectingRef.current) setIsReconnecting(true);
          }, 3000);
          // Debounce: reset liveness so the next tick gives the fresh stream a
          // full staleness window before re-triggering.
          lastEventAtRef.current = Date.now();
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          connectedRunIdRef.current = null;
          void reconnectToRun(activeRunId, { force: true });
        }
      })();
    }, VERIFY_INTERVAL_MS);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
    };
  }, [
    isRunning,
    runId,
    workflowId,
    nodeStatuses,
    hydrateFromSteps,
    reconnectToRun,
    clearReconnectHint,
  ]);

  return {
    isRunning,
    isReconnecting,
    runId,
    events,
    currentStepId,
    nodeStatuses,
    completedNodeIds,
    failedNodeIds,
    screenshotItems,
    startExecution,
    stopExecution,
    reconnectToRun,
    checkForActiveRun,
  };
}
